  import {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
  } from "react";
  import Select from "react-select";
  import DatePicker from "react-datepicker";
  import moment from "moment";
  import toast from "react-hot-toast";
  import {
    IntakeSheetSession,
    ChiropracticCareConsentConstant as CCC,
    RoleType,
    CurrentProviderData,
  } from "../../../helpers/constant";
  import { getSessionStorage } from "../../../helpers/storage";

  import SignaturePad from "../../../Component/shared/signaturePad";
  import SpinnerLoader from "../../../Component/shared/loader/spinnerLoader/SpinnerLoader";
  import "./ChiropracticCareConsent.css";
  import { GragIntakeSheetData } from "../IntakeSheetUtils";
  import { GetCaseFormListData, SaveCaseFormData } from "../Service";
  import { usePatientData } from "../../../commonFunction/storage/Patientdatacontext";



  /* ═══════════════════════════════════════════════════════
    ChiropracticCareConsent
  ═══════════════════════════════════════════════════════ */
  const ChiropracticCareConsent = forwardRef(function ChiropracticCareConsent(props, ref) {
    const {
      providerOptions = [],
      physicianOptions = [],
      sessionData = {},
      signatureMiss,
      copySignature,
    } = props;

    const tokenData    = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
    const providerData = getSessionStorage(CurrentProviderData) || [];
    const roleName     = sessionData?.Role?.Name?.trim().toLowerCase() ?? "";
    const isFrontDesk  = roleName === "frontdesk" || roleName === "front desk" || roleName === "admin";

    /* ── state ── */
    const [loading, setLoading]                     = useState(false);
    const [caseFormDataId, setCaseFormDataId]       = useState("");
    const [collectionOfCCC, setCollectionOfCCC]     = useState([]);
    const [beforeParsingCCC, setBeforeParsingCCC]   = useState([]);

    // form fields
    const [patientName, setPatientName]             = useState("");
    const [patientAge, setPatientAge]               = useState("");
    const [otherText, setOtherText]                 = useState("");
    const [doctorName, setDoctorName]               = useState("");
    const [legalPersonName, setLegalPersonName]     = useState("");
    const [legalPersonRelation, setLegalPersonRelation] = useState("");
    const [formDate, setFormDate]                   = useState(new Date());
    const [providerName, setProviderName]           = useState("");
    const [providerAddress, setProviderAddress]     = useState("");

    // provider / physician selects
    const [selectedProvider, setSelectedProvider]   = useState(providerOptions[0] || null);
    const [selectPhysician, setSelectPhysician]     = useState(physicianOptions[0] || null);
    const [isSelectedProvider, setIsSelectedProvider] = useState(false);

    // signatures
    const [patientSignImg, setPatientSignImg]       = useState("");
    const [providerSignImg, setProviderSignImg]     = useState("");
    const [attorneySignImg, setAttorneySignImg]     = useState("");
    const [sigPadOpen, setSigPadOpen]               = useState(false);
    const [signRole, setSignRole]                   = useState("");


    const { patientData, fetchPatientData } = usePatientData();
  
    const stateRef = useRef({});
    stateRef.current = {
      collectionOfCCC,
      beforeParsingCCC,
      caseFormDataId,
      patientName,
      patientAge,
      otherText,
      doctorName,
      legalPersonName,
      legalPersonRelation,
      formDate,
      providerName,
      providerAddress,
      selectedProvider,
      selectPhysician,
      patientSignImg,
      providerSignImg,
      attorneySignImg,
    };

    const syncField = (name, value) => {
      stateRef.current[name] = value;
      if (stateRef.current.collectionOfCCC) {
        const col = stateRef.current.collectionOfCCC;
        const item = col.find((x) => x.Name === name);
        if (item) {
          item.Value = value !== undefined && value !== null ? value : "";
        } else {
          col.push({
            Name: name,
            Type: "text",
            Value: value !== undefined && value !== null ? value : "",
            isRender: true,
            RenderKeyName: col[0]?.RenderKeyName || "ChiropracticCareConsent",
          });
        }
      }
    };

    const handleOtherTextChange = (e) => {
      const val = e.target.value;
      setOtherText(val);
      stateRef.current.otherText = val;
      syncField(CCC.OtherText, val);
    };

    const handleLegalPersonNameChange = (e) => {
      const val = e.target.value;
      setLegalPersonName(val);
      stateRef.current.legalPersonName = val;
      syncField(CCC.LegalPersonName, val);
    };

    const handleLegalPersonRelationChange = (e) => {
      const val = e.target.value;
      setLegalPersonRelation(val);
      stateRef.current.legalPersonRelation = val;
      syncField(CCC.LegalPersonRelation, val);
    };

    const handlePatientAgeChange = (e) => {
      const val = e.target.value;
      if (/^[0-9]*$/.test(val)) {
        setPatientAge(val);
        stateRef.current.patientAge = val;
        syncField(CCC.PatientAge, val);
      }
    };

    const handleFormDateChange = (d) => {
      setFormDate(d);
      stateRef.current.formDate = d;
      const fmtVal = d == null || d === "" ? "" : moment(d).format("MM-DD-YYYY").split("-").join("/");
      syncField(CCC.FormDate, fmtVal);
    };

    /* ── imperative handle ── */
    useImperativeHandle(ref, () => ({
      save:           () => saveFunction(),
      isPending:      () => !stateRef.current.patientSignImg,
      getSignature:   () => stateRef.current.patientSignImg || "",
      applySignature: (sig) => {
        if (!sig) return;
        setPatientSignImg(sig);
        setFormDate(new Date());
        stateRef.current.patientSignImg = sig;
        stateRef.current.formDate = new Date();
        syncField(CCC.PatientSign, sig);
        syncField(CCC.FormDate, moment(new Date()).format("MM-DD-YYYY").split("-").join("/"));
      },
    }));

    useEffect(() => {
      props?.onReady?.("chiro_consent");
    }, [patientSignImg]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      callApi();
    }, [patientData]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ══════════════════════════════════════════════════
      HELPERS
    ══════════════════════════════════════════════════ */
    const createAddressText = (prov) => {
      if (!prov) return "";
      return [prov.Address, prov.City, prov.Street, prov.State, prov.Zip]
        .filter(Boolean).join(", ");
    };

    const normalise = (raw) => {
      if (!raw) return "";
      return raw.includes(IntakeSheetSession.dataBlock)
        ? raw
        : IntakeSheetSession.dataBlock + raw;
    };

    const parseDateSafe = (raw) =>
      isNaN(new Date(raw).getTime()) ? "" : new Date(raw);

    /* ══════════════════════════════════════════════════
      FETCH
    ══════════════════════════════════════════════════ */
    const callApi = async () => {
      setLoading(true);
      const formData    = await handleGetCaseFormData();
      const patientInfo = patientData || await fetchPatientData(tokenData?.caseId, tokenData?.companyId);

      if (!formData?.JsonData) {
        console.error("No JsonData returned");
        setLoading(false);
        return;
      }

      const cccData    = JSON.parse(formData.JsonData);
      const collection = GragIntakeSheetData(cccData);

      setCollectionOfCCC(collection);
      setBeforeParsingCCC(cccData);
      setCaseFormDataId(formData.CaseFormDataId || "");

      bindValueToControl(collection, patientInfo);
      setLoading(false);
    };

    const handleGetCaseFormData = async () => {
      try {
        const requestBody = {
          CaseFormDataId:  null,
          CaseId:          tokenData.caseId,
          CompanyId:       tokenData.companyId,
          DoctorId:        "ALL",
          EntityLevel:     0,
          FormId:          33,              // ← ChiropracticCareConsent FormId
          FormName:        "Chiropractic Care Consent",
          FormType:        null,
          IsFormCompleted: false,
          JsonData:        null,
          TemplateName:    null,
          UserId: "US000000000000012172",
        
        };
        const response = await GetCaseFormListData(requestBody);
        if (response?.HasException) {
          toast.error(`Something went wrong. ${response?.MessageText}. ${response?.Exception}`);
          return {};
        }
        return response?.Data?.[0] || {};
      } catch (error) {
        toast.error(error.message);
        return {};
      }
    };


    /* ══════════════════════════════════════════════════
      BIND VALUES
    ══════════════════════════════════════════════════ */
    const bindValueToControl = (collection, patientInfo) => {
      if (!patientInfo) return;

      const getValue = (key) =>
        collection.find((x) => x.Name === key)?.Value || "";

      // Patient info
      const fullName = [
        patientInfo.patientFirstName,
        patientInfo.patientMiddleName,
        patientInfo.patientLastName,
      ].filter(Boolean).join(" ");

      setPatientName(getValue(CCC.PatientName) || fullName);
      setPatientAge(patientInfo.patientAge || getValue(CCC.PatientAge) || "");
      setOtherText(getValue(CCC.OtherText));
      setLegalPersonName(getValue(CCC.LegalPersonName));
      setLegalPersonRelation(getValue(CCC.LegalPersonRelation));
      setFormDate(parseDateSafe(getValue(CCC.FormDate)) || new Date());

      // Signatures
      const savedSig = copySignature
        ? sessionStorage.getItem("sharedPatientSignature")
        : null;
      const patSig = savedSig || normalise(getValue(CCC.PatientSign));
      setPatientSignImg(patSig);
      setProviderSignImg(normalise(getValue(CCC.DoctorSign)));
      setAttorneySignImg(normalise(getValue(CCC.LegalPersonSign)));

      if (!patSig) signatureMiss?.(true);

      // Provider
      if (isFrontDesk) {
        const savedProvName = getValue(CCC.ProviderName);
        const provider =
          providerOptions.find((x) => x.label === savedProvName) ||
          providerOptions[0] ||
          null;
        const pData = providerData.find((x) => x.ProviderId === provider?.value);
        setSelectedProvider(provider);
        setProviderName(provider?.label || "");
        setProviderAddress(pData ? createAddressText(pData) : "");

        // Physician
        const savedDocName = getValue(CCC.DoctorName);
        const physician =
          physicianOptions.find((x) => x.doctorName === savedDocName) ||
          physicianOptions[0] ||
          null;
        setSelectPhysician(physician);
        setDoctorName(physician?.doctorName || "");
      } else {
        const prov = providerData[0] || {};
      setProviderName(
      getValue(CCC.ProviderName) ||  
      prov.ProviderName ||
      tokenData?.providerName ||
      ""
    );
        setProviderAddress(createAddressText(prov));
        setDoctorName(
          getValue(CCC.DoctorName) ||
          tokenData?.providerName ||
          ""
        );
      }

      setLoading(false);
    };

    /* ══════════════════════════════════════════════════
      PROVIDER / PHYSICIAN CHANGE
    ══════════════════════════════════════════════════ */
    const onProviderChange = (prov) => {
      if (!prov) return;
      const pData = providerData.find((x) => x.ProviderId === prov.value);
      setSelectedProvider(prov);
      setProviderName(prov.label || "");
      setProviderAddress(pData ? createAddressText(pData) : "");
      setIsSelectedProvider(false);

      stateRef.current.selectedProvider = prov;
      stateRef.current.providerName = prov.label || "";
      stateRef.current.providerAddress = pData ? createAddressText(pData) : "";
      syncField(CCC.ProviderName, prov.label || "");
      syncField(CCC.ProviderAddress, pData ? createAddressText(pData) : "");
    };

    const onPhysicianChange = (physician) => {
      if (!physician) return;
      setSelectPhysician(physician);
      setDoctorName(physician.doctorName || "");

      stateRef.current.selectPhysician = physician;
      stateRef.current.doctorName = physician.doctorName || "";
      syncField(CCC.DoctorName, physician.doctorName || "");
    };

    /* ══════════════════════════════════════════════════
      UPDATE COLLECTION
    ══════════════════════════════════════════════════ */
    const updateCollection = (collection, vals) => {
      const cur = vals || stateRef.current;
      const base = collection || cur.collectionOfCCC || [];
      const updated = base.map((x) => ({ ...x }));
      const defaultRenderKey =
        updated[0]?.RenderKeyName ||
        cur.beforeParsingCCC?.notesdata?.[0]?.RenderKeyName ||
        "ChiropracticCareConsent";

      const set = (key, value) => {
        const item = updated.find((x) => x.Name === key);
        if (item) {
          item.Value = value !== undefined && value !== null ? value : "";
        } else {
          updated.push({
            Name: key,
            Type: "text",
            Value: value !== undefined && value !== null ? value : "",
            isRender: true,
            RenderKeyName: defaultRenderKey,
          });
        }
      };

      const fmt = (d) =>
        d == null || d === ""
          ? ""
          : moment(d).format("MM-DD-YYYY").split("-").join("/");

      const resolvedProvName = isFrontDesk
        ? providerOptions.length === 1
          ? providerOptions[0]?.label || ""
          : cur.selectedProvider?.label || ""
        : providerData[0]?.ProviderName || cur.providerName;

      const resolvedDocName = isFrontDesk
        ? cur.selectPhysician?.doctorName || cur.doctorName
        : cur.doctorName;

      set(CCC.PatientName,         cur.patientName);
      set(CCC.PatientAge,          cur.patientAge);
      set(CCC.OtherText,           cur.otherText);
      set(CCC.ProviderName,        resolvedProvName);
      set(CCC.ProviderAddress,     cur.providerAddress);
      set(CCC.DoctorName,          resolvedDocName);
      set(CCC.LegalPersonName,     cur.legalPersonName);
      set(CCC.LegalPersonRelation, cur.legalPersonRelation);
      set(CCC.PatientSign,         cur.patientSignImg);
      set(CCC.DoctorSign,          cur.providerSignImg);
      set(CCC.LegalPersonSign,     cur.attorneySignImg);
      set(CCC.FormDate,            fmt(cur.formDate));

      return updated;
    };

    /* ══════════════════════════════════════════════════
      SAVE API
    ══════════════════════════════════════════════════ */
    const callSaveApi = async (jsonData, targetCaseFormDataId, curState) => {
      try {
        setLoading(true);
        const cur = curState || stateRef.current;

        const doctorId = isFrontDesk
          ? (providerOptions.length === 1
              ? providerOptions[0]?.value
              : cur.selectedProvider?.value) || ""
          : providerData[0]?.ProviderId || tokenData?.doctorId || "";

        const payload = {
          CaseFormDataId:  targetCaseFormDataId || cur.caseFormDataId || null,
          CaseId:          tokenData.caseId,
          CompanyId:       tokenData.companyId,
          DoctorId:        doctorId,
          EntityLevel:     0,
          FormId:          33,
          FormName:        "Chiropractic Care Consent",
          FormType:        null,
          IsChanged:       false,
          IsCopySign:      false,
          IsFormCompleted: false,
          TemplateName:    null,
          UserId:          tokenData?.userId,
          officeId:        tokenData?.providerId,
          JsonData:        jsonData,
        };

        const response = await SaveCaseFormData(payload);

        if (response?.HasException) {
          toast.error("Error while saving Chiropractic Care Consent.");
          return false;
        }
        if (response?.Data?.[0]?.CaseFormDataId) {
          setCaseFormDataId(response.Data[0].CaseFormDataId);
          stateRef.current.caseFormDataId = response.Data[0].CaseFormDataId;
        }
        toast.success("Chiropractic Care Consent saved successfully.");
        return true;
      } catch (err) {
        console.error("callSaveApi error:", err);
        toast.error("Error while saving Chiropractic Care Consent.");
        return false;
      } finally {
        setLoading(false);
      }
    };

    /* ══════════════════════════════════════════════════
      FORM JSON REQUEST
    ══════════════════════════════════════════════════ */
    const formJsonRequest = async (updatedCollection, beforeData, targetCaseFormDataId, curState) => {
      try {
        if (!beforeData || !beforeData.notesdata || beforeData.notesdata.length === 0) {
          console.error("No notesdata found in beforeData");
          beforeData = {
            notesdata: [
              {
                RenderKeyName: updatedCollection[0]?.RenderKeyName || "ChiropracticCareConsent",
                ApplicationValue: [],
              },
            ],
          };
        }

        const signFields = [CCC.PatientSign, CCC.DoctorSign, CCC.LegalPersonSign];

        // Collect all existing names across all sections
        const allExistingNames = new Set();
        for (let i = 0; i < beforeData.notesdata.length; i++) {
          const section = beforeData.notesdata[i];
          if (!section.ApplicationValue) section.ApplicationValue = [];
          for (let j = 0; j < section.ApplicationValue.length; j++) {
            allExistingNames.add(section.ApplicationValue[j].Name);
          }
        }

        // 1. Update any existing field by Name in whichever section it already lives
        for (let i = 0; i < beforeData.notesdata.length; i++) {
          const section = beforeData.notesdata[i];
          for (let j = 0; j < section.ApplicationValue.length; j++) {
            const appVal = section.ApplicationValue[j];
            const matchItem = updatedCollection.find((x) => x.Name === appVal.Name);
            if (!matchItem) continue;

            appVal.Type = matchItem.Type || appVal.Type || "text";
            appVal.isRender = matchItem.isRender !== undefined ? matchItem.isRender : appVal.isRender;

            const isSign = signFields.includes(appVal.Name);
            if (isSign) {
              const val = matchItem.Value;
              if (val) {
                appVal.Value = val.includes(";") ? val.split(";")[1].split(",")[1] : val;
              } else {
                appVal.Value = "";
              }
            } else {
              appVal.Value = matchItem.Value !== undefined && matchItem.Value !== null ? matchItem.Value : "";
            }
          }
        }

        // 2. Append missing items from updatedCollection that do not exist in any section
        for (const item of updatedCollection) {
          if (allExistingNames.has(item.Name)) continue;

          // Find target section by RenderKeyName, or fallback to the first section
          let targetSection = beforeData.notesdata.find(
            (sec) => sec.RenderKeyName === item.RenderKeyName
          );
          if (!targetSection) {
            targetSection = beforeData.notesdata[0];
          }

          const isSign = signFields.includes(item.Name);
          let val = item.Value !== undefined && item.Value !== null ? item.Value : "";
          if (isSign && val && val.includes(";")) {
            val = val.split(";")[1].split(",")[1];
          }

          targetSection.ApplicationValue.push({
            Name: item.Name,
            Type: item.Type || "text",
            Value: val,
            isRender: item.isRender ?? true,
          });
          allExistingNames.add(item.Name);
        }

        const result = await callSaveApi(JSON.stringify(beforeData), targetCaseFormDataId, curState);
        setCollectionOfCCC(updatedCollection);
        setBeforeParsingCCC(beforeData);
        stateRef.current.collectionOfCCC = updatedCollection;
        stateRef.current.beforeParsingCCC = beforeData;
        return result;
      } catch (error) {
        console.error("formJsonRequest error:", error.message);
        return false;
      }
    };

    /* ══════════════════════════════════════════════════
      SAVE FUNCTION
    ══════════════════════════════════════════════════ */
    const saveFunction = async () => {
      const cur = stateRef.current;

      // Provider validation
      if (isFrontDesk && providerOptions.length > 1 && !cur.selectedProvider) {
        toast.error("Provider is required");
        setIsSelectedProvider(true);
        return false;
      }

      setLoading(true);
      const updatedCollection = updateCollection(cur.collectionOfCCC, cur);
      const beforeDataClone =
        cur.beforeParsingCCC && cur.beforeParsingCCC.notesdata && cur.beforeParsingCCC.notesdata.length > 0
          ? JSON.parse(JSON.stringify(cur.beforeParsingCCC))
          : { notesdata: [{ RenderKeyName: "ChiropracticCareConsent", ApplicationValue: [] }] };
      const result = await formJsonRequest(updatedCollection, beforeDataClone, cur.caseFormDataId, cur);
      setLoading(false);
      return result;
    };

    /* ══════════════════════════════════════════════════
      SIGNATURE HANDLERS
    ══════════════════════════════════════════════════ */
    const clickSign = (role) => { setSignRole(role); setSigPadOpen(true); };

    const signUpdatedCall = (base64Img) => {
      const newBase = base64Img.includes(IntakeSheetSession.dataBlock1)
        ? base64Img.replace(IntakeSheetSession.dataBlock1, IntakeSheetSession.dataBlock)
        : base64Img;

      if (signRole === RoleType.Patient) {
        setPatientSignImg(newBase);
        setFormDate(new Date());
        stateRef.current.patientSignImg = newBase;
        stateRef.current.formDate = new Date();
        syncField(CCC.PatientSign, newBase);
        syncField(CCC.FormDate, moment(new Date()).format("MM-DD-YYYY").split("-").join("/"));
        sessionStorage.setItem("sharedPatientSignature", newBase);
      }
      if (signRole === RoleType.Doctor) {
        setProviderSignImg(newBase);
        stateRef.current.providerSignImg = newBase;
        syncField(CCC.DoctorSign, newBase);
      }
      if (signRole === RoleType.Attorney) {
        setAttorneySignImg(newBase);
        stateRef.current.attorneySignImg = newBase;
        syncField(CCC.LegalPersonSign, newBase);
      }
    };

    const clearSignCall = () => {
      if (signRole === RoleType.Patient) {
        setPatientSignImg("");
        stateRef.current.patientSignImg = "";
        syncField(CCC.PatientSign, "");
        signatureMiss?.(true);
      }
      if (signRole === RoleType.Doctor) {
        setProviderSignImg("");
        stateRef.current.providerSignImg = "";
        syncField(CCC.DoctorSign, "");
      }
      if (signRole === RoleType.Attorney) {
        setAttorneySignImg("");
        stateRef.current.attorneySignImg = "";
        syncField(CCC.LegalPersonSign, "");
      }
    };

    /* ══════════════════════════════════════════════════
      PROVIDER FIELD
    ══════════════════════════════════════════════════ */
    const ProviderField = () =>
      providerOptions.length === 1 ? (
        <input
          className="ccc__input"
          type="text"
          value={providerOptions[0]?.label || ""}
          readOnly
        />
      ) : (
        <Select
          options={providerOptions}
          onChange={onProviderChange}
          value={selectedProvider}
          getOptionLabel={(e) => e?.label}
          getOptionValue={(e) => e?.value}
          tabSelectsValue={false}
          classNamePrefix={`react-select${isSelectedProvider ? " react-select--error" : ""}`}
          placeholder="Select Provider"
        />
      );

    /* ── Physician field (FrontDesk only) ── */
    const PhysicianField = ({ grow } = {}) =>
      isFrontDesk ? (
        <Select
          options={physicianOptions}
          onChange={onPhysicianChange}
          value={selectPhysician}
          getOptionLabel={(e) => e?.doctorName}
          getOptionValue={(e) => e?.doctorId}
          tabSelectsValue={false}
          classNamePrefix="react-select"
          placeholder="Select Physician"
          className={grow ? "ccc__select--grow" : ""}
        />
      ) : (
        <input
          className={`ccc__input${grow ? " ccc__input--grow" : ""}`}
          type="text"
          value={doctorName}
          disabled
        />
      );

    /* ══════════════════════════════════════════════════
      RENDER
    ══════════════════════════════════════════════════ */
    return (
      <>
        {loading ? (
          <SpinnerLoader message="Loading form data…" />
        ) : (
          <div className="ccc__wrapper">

            {/* ── Provider + address (top) ── */}
            <div className="ccc__provider-top">
              <div className="ccc__provider-field">
                {isFrontDesk ? <ProviderField /> : (
                  <input
                    className="ccc__input ccc__input--grow"
                    type="text"
                    value={providerName}
                  //   onChange={(e) => setProviderName(e.target.value)}
                    disabled
                  />
                )}
              </div>
              {providerAddress && (
                <div className="ccc__provider-address">{providerAddress}</div>
              )}
            </div>

            {/* ── Title ── */}
            <div className="ccc__title-block">
              <div className="ccc__title-main">INFORMED CONSENT TO CHIROPRACTIC CARE</div>
            </div>

            {/* ── Intro note ── */}
            <div className="ccc__note">
              <b>Patient: Please discuss any questions or concerns with the doctor before signing the consent.</b>
            </div>

            {/* ── Legal paragraphs ── */}
            <div className="ccc__legal-para">
              I hereby request and consent to the performance of chiropractic adjustments and other chiropractic
              procedures on me (or on the patient named below) by the doctor of chiropractic named below.
            </div>

            <div className="ccc__legal-para">
              I have been informed of the nature of my disorder(s) and of the nature and purpose of chiropractic
              procedures and related therapeutics proposed as a treatment. Alternatives to treatment have been reviewed.
            </div>

            <div className="ccc__legal-para">
              I understand and am informed that, though chiropractic adjustments and treatments are usually beneficial
              and seldom cause any problems, there are some risks including but not limited to, muscle strains and
              sprains, fractures, dislocations, disc injuries and strokes.
            </div>

            <div className="ccc__legal-para">
              I understand that chiropractic is not an exact science and that, therefore, reputable practitioners
              cannot fully guarantee results. I acknowledge that no guarantee or assurance has been made by anyone
              regarding the chiropractic treatment that I have requested and authorized. I have had the opportunity
              to read this form and ask questions. My questions have been answered and I consent to the proposed treatment.
            </div>

            {/* ── I THEREFORE AUTHORIZE ── */}
            <div className="ccc__authorize-row">
              <span className="ccc__label"><b>I, THEREFORE AUTHORIZE</b></span>
              <div className="ccc__physician-field">
                <PhysicianField grow />
              </div>
              <span className="ccc__label"><b>TO PROCEED WITH CHIROPRACTIC CARE AND TREATMENT.</b></span>
            </div>

            {/* ── Patient / Doctor name grid ── */}
            <div className="ccc__grid-row">
              <span className="ccc__grid-label"><b>Name:</b></span>
              <input
                className="ccc__input"
                type="text"
                value={patientName}
                onChange={(e) => {
                  setPatientName(e.target.value);
                  stateRef.current.patientName = e.target.value;
                }}
                disabled
              />
              <span className="ccc__grid-label"><b>Name of the Doctor:</b></span>
              <div className="ccc__physician-field">
                <PhysicianField grow />
              </div>
            </div>

            {/* ── Signature boxes: Patient / Physician ── */}
            <div className="ccc__grid-row">
              <span className="ccc__grid-label"><b>Signature:</b></span>
              <div
                className={`ccc__sign-box${!patientSignImg ? " ccc__sign-box--empty" : ""}`}
                onClick={() => clickSign(RoleType.Patient)}
              >
                {patientSignImg
                  ? <img src={patientSignImg} alt="Patient signature" className="ccc__sign-img" />
                  : <span className="ccc__sign-hint">Click to sign</span>}
              </div>

              <span className="ccc__grid-label"><b>Signature of Physician:</b></span>
              <div
                className="ccc__sign-box ccc__sign-box--disabled"
              >
                {providerSignImg && (
                  <img src={providerSignImg} alt="Physician signature" className="ccc__sign-img" />
                )}
              </div>
            </div>

            {/* ── Minor section ── */}
            <div className="ccc__section-label">
              <b>WHEN A PATIENT IS A MINOR:</b>
            </div>

            {/* ── Patient Age ── */}
            <div className="ccc__sentence-row">
              <span className="ccc__label"><b>Patient Age:</b></span>
              <input
                className="ccc__input ccc__input--sm"
                type="text"
                inputMode="numeric"
                maxLength={3}
                value={patientAge}
                onChange={handlePatientAgeChange}
                disabled
              />
            </div>

            {/* ── Other ── */}
            <div className="ccc__sentence-row">
              <span className="ccc__label"><b>Other:</b></span>
              <input
                className="ccc__input ccc__input--grow"
                type="text"
                value={otherText}
                onChange={handleOtherTextChange}
                placeholder="Other Information"
              />
            </div>

            {/* ── Patient Name (minor section) ── */}
            <div className="ccc__sentence-row">
              <span className="ccc__label"><b>Patient Name:</b></span>
              <input
                className="ccc__input ccc__input--grow"
                type="text"
                value={patientName}
                disabled
              />
            </div>

            {/* ── Legal person name ── */}
            <div className="ccc__sentence-row">
              <span className="ccc__label"><b>Person legally authorized to sign for the patient:</b></span>
              <input
                className="ccc__input ccc__input--grow"
                type="text"
                value={legalPersonName}
                onChange={handleLegalPersonNameChange}
                placeholder="Legal Person Name"
              />
            </div>

            {/* ── Relationship ── */}
            <div className="ccc__sentence-row">
              <span className="ccc__label"><b>Relationship:</b></span>
              <input
                className="ccc__input ccc__input--grow"
                type="text"
                value={legalPersonRelation}
                onChange={handleLegalPersonRelationChange}
                placeholder="Relation to patient"
              />
            </div>

            {/* ── Legal person signature ── */}
            <div className="ccc__sentence-row">
              <span className="ccc__label"><b>Signature of authorized person:</b></span>
              <div
                className={`ccc__sign-box${!attorneySignImg ? " ccc__sign-box--empty" : ""}`}
                onClick={() => clickSign(RoleType.Attorney)}
              >
                {attorneySignImg ? (
                  <img src={attorneySignImg} alt="Authorized person signature" className="ccc__sign-img" />
                ) : (
                  <span className="ccc__sign-hint">Click to sign</span>
                )}
              </div>
            </div>

            {/* ── Date ── */}
            <div className="ccc__sentence-row">
              <span className="ccc__label"><b>Date:</b></span>
              <DatePicker
                selected={formDate}
                onChange={handleFormDateChange}
                dateFormat="MM/dd/yyyy"
                className="ccc__datepicker"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
            </div>

            {/* ── Signature Pad Modal ── */}
            <SignaturePad
              isOpen={sigPadOpen}
              title={
                signRole === RoleType.Patient  ? "Patient Signature"   :
                signRole === RoleType.Doctor   ? "Physician Signature" :
                                                "Authorized Person Signature"
              }
              existingSign={
                signRole === RoleType.Patient  ? patientSignImg  :
                signRole === RoleType.Doctor   ? providerSignImg :
                                                attorneySignImg
              }
              onClose={() => setSigPadOpen(false)}
              onSave={(base64) => { signUpdatedCall(base64); setSigPadOpen(false); }}
              onClear={clearSignCall}
            />

          </div>
        )}
      </>
    );
  });

  export default ChiropracticCareConsent;